(() => {
  const VERSION = 'home-story-scoped-no-sticky-v1';
  const ROOT_SELECTOR = '[data-cd-home-story]:not([data-cd-about-story-sticky])';

  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const state = {
    roots: new Set(),
    raf: 0,
    viewportH: Math.max(window.innerHeight || 0, 1),
  };

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function updateViewport() {
    state.viewportH = Math.max(window.innerHeight || document.documentElement.clientHeight || 1, 1);
  }

  function removeLegacyStickyRails() {
    document.querySelectorAll('.cd-story-sticky-rails').forEach((rails) => rails.remove());

    document.querySelectorAll('.cd-home-story-group').forEach((group) => {
      const parent = group.parentNode;
      if (!parent) return;
      while (group.firstChild) parent.insertBefore(group.firstChild, group);
      group.remove();
    });
  }

  function buildLineflow(root) {
    root.querySelectorAll('[data-cd-story-lineflow]').forEach((element) => {
      if (element.dataset.cdStoryLineflowReady === 'true') return;

      const original = element.dataset.cdStoryOriginal || element.textContent || '';
      element.dataset.cdStoryOriginal = original;
      element.textContent = '';

      const line = document.createElement('span');
      line.className = 'cd-story-line';

      const inner = document.createElement('span');
      inner.className = 'cd-story-line__text';
      inner.style.setProperty('--cd-story-line-index', '0');
      inner.textContent = original;

      line.appendChild(inner);
      element.appendChild(line);
      element.dataset.cdStoryLineflowReady = 'true';
    });
  }

  function applyTimeline(root) {
    const rect = root.getBoundingClientRect();
    const vh = state.viewportH;
    const total = Math.max(rect.height + vh, 1);
    const raw = clamp((vh - rect.top) / total, 0, 1);
    const enter = clamp((vh - rect.top) / (vh * 0.9), 0, 1);
    const exit = clamp((-rect.top) / Math.max(rect.height - vh * 0.28, 1), 0, 1);

    root.style.setProperty('--cd-story-scroll-progress', raw.toFixed(4));
    root.style.setProperty('--cd-story-enter-progress', enter.toFixed(4));
    root.style.setProperty('--cd-story-exit-progress', exit.toFixed(4));
    root.style.setProperty('--cd-story-media-y', `${((0.5 - raw) * 18).toFixed(2)}px`);
    root.style.setProperty('--cd-story-media-scale', (1 + enter * 0.025 - exit * 0.012).toFixed(4));
    root.style.setProperty('--cd-story-image-scale', (1.08 - enter * 0.08).toFixed(4));

    root.querySelectorAll('[data-cd-story-reveal]').forEach((item) => {
      const offset = Number.parseFloat(item.dataset.cdStoryRevealOffset || '0');
      const local = clamp((enter - offset) / 0.48, 0, 1);
      const fadeOut = clamp((exit - 0.76) / 0.24, 0, 1);
      item.style.setProperty('--cd-story-reveal-opacity', (local * (1 - fadeOut * 0.35)).toFixed(4));
      item.style.setProperty('--cd-story-reveal-y', `${((1 - local) * 34).toFixed(2)}px`);
      item.style.setProperty('--cd-story-reveal-scale', (0.965 + local * 0.035).toFixed(4));
    });
  }

  function createRevealObserver(root) {
    if (!('IntersectionObserver' in window) || prefersReduced) {
      root.querySelectorAll('[data-cd-story-block-reveal]').forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        threshold: 0.18,
        rootMargin: '0px 0px -12% 0px',
      }
    );

    root.querySelectorAll('[data-cd-story-block-reveal]').forEach((element) => observer.observe(element));
    root.__cdHomeStoryRevealObserver = observer;
  }

  function registerSection(root) {
    if (!root || root.dataset.cdHomeStoryInit === VERSION) return;
    if (root.matches('[data-cd-about-story-sticky]')) return;

    root.dataset.cdHomeStoryInit = VERSION;
    root.dataset.cdStoryScope = root.dataset.cdStoryScope || 'home';

    removeLegacyStickyRails();
    buildLineflow(root);
    createRevealObserver(root);
    state.roots.add(root);
    schedule();
  }

  function scan(context = document) {
    context.querySelectorAll(ROOT_SELECTOR).forEach(registerSection);
    if (context.matches && context.matches(ROOT_SELECTOR)) registerSection(context);
  }

  function render() {
    state.raf = 0;

    if (prefersReduced) {
      state.roots.forEach((root) => {
        root.querySelectorAll('[data-cd-story-block-reveal]').forEach((element) => element.classList.add('is-visible'));
      });
      return;
    }

    state.roots.forEach((root) => {
      if (!document.documentElement.contains(root)) {
        state.roots.delete(root);
        return;
      }
      applyTimeline(root);
    });
  }

  function schedule() {
    if (state.raf) return;
    state.raf = window.requestAnimationFrame(render);
  }

  function boot() {
    updateViewport();
    removeLegacyStickyRails();
    scan(document);
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => {
    updateViewport();
    schedule();
  }, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    removeLegacyStickyRails();
    scan(event.target || document);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target && event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
    if (!root) return;
    if (root.__cdHomeStoryRevealObserver) root.__cdHomeStoryRevealObserver.disconnect();
    state.roots.delete(root);
  });

  const observer = new MutationObserver((mutations) => {
    let shouldScan = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if ((node.matches && node.matches(ROOT_SELECTOR)) || (node.querySelector && node.querySelector(ROOT_SELECTOR))) {
          shouldScan = true;
        }
      });
    });
    if (shouldScan) scan(document);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
